import { SxProps } from '@mui/material'

import theme from '../../styles/themes/default.theme'

const violet = {
  primary: theme.palette.primary.main,
  light: theme.palette.primary.light,
  soft: '#F5F3FF',
  softer: '#FAF8FF',
  dot: '#8B5CF6',
  dotGlow: 'rgba(139, 92, 246, 0.35)',
  dotRing: 'rgba(139, 92, 246, 0.28)',
  border: 'rgba(109, 40, 217, 0.18)',
  buttonBorder: 'rgba(109, 40, 217, 0.22)',
  shadowTint: 'rgba(109, 40, 217, 0.14)',
  focus: 'rgba(109, 40, 217, 0.45)',
  hoverShadow: 'rgba(109, 40, 217, 0.22)'
}

const ink = {
  primary: theme.palette.text.primary,
  secondary: theme.palette.text.secondary,
  muted: 'rgba(0, 0, 0, 0.32)',
  refreshRest: '#4C1D95',
  refreshHover: '#FFFFFF'
}

const styles = {
  anchor: {
    position: 'fixed',
    bottom: 20,
    right: 20,
    zIndex: theme.zIndex.snackbar - 1,
    pointerEvents: 'none',
    '@keyframes chip-dot-pulse': {
      '0%, 100%': { opacity: 1, transform: 'scale(1)' },
      '50%': { opacity: 0.65, transform: 'scale(0.82)' }
    },
    '@keyframes chip-dot-ring': {
      '0%': { opacity: 0.55, transform: 'scale(1)' },
      '100%': { opacity: 0, transform: 'scale(2.6)' }
    }
  } as SxProps,

  chip: {
    pointerEvents: 'auto',
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    height: 40,
    paddingLeft: '14px',
    paddingRight: '6px',
    borderRadius: '999px',
    backgroundColor: theme.palette.background.paper,
    color: ink.primary,
    fontFamily: 'DM Sans, Roboto, system-ui, -apple-system, sans-serif',
    boxShadow: [
      '0 1px 0 rgba(255,255,255,0.9) inset',
      `0 20px 40px -16px ${violet.shadowTint}`,
      '0 8px 18px -6px rgba(16, 17, 28, 0.10)',
      '0 1px 2px rgba(16, 17, 28, 0.04)'
    ].join(', '),
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      padding: '1px',
      background: `linear-gradient(135deg, ${violet.border} 0%, rgba(0,0,0,0.05) 48%, rgba(0,0,0,0.02) 100%)`,
      WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
      WebkitMaskComposite: 'xor',
      mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
      maskComposite: 'exclude',
      pointerEvents: 'none'
    }
  } as SxProps,

  dot: {
    position: 'relative',
    width: 7,
    height: 7,
    marginLeft: '2px',
    borderRadius: '50%',
    backgroundColor: violet.dot,
    boxShadow: `0 0 10px ${violet.dotGlow}`,
    animation: 'chip-dot-pulse 1.8s ease-in-out infinite',
    flexShrink: 0,
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: -2,
      borderRadius: '50%',
      backgroundColor: violet.dotRing,
      animation: 'chip-dot-ring 1.8s ease-out infinite',
      transformOrigin: 'center'
    }
  } as SxProps,

  label: {
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: '-0.003em',
    color: ink.primary,
    lineHeight: 1,
    whiteSpace: 'nowrap'
  } as SxProps,

  refreshButton: {
    all: 'unset',
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: 28,
    paddingLeft: '10px',
    paddingRight: '12px',
    marginLeft: '2px',
    borderRadius: '999px',
    fontFamily: 'DM Sans, Roboto, system-ui, sans-serif',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: ink.refreshRest,
    backgroundColor: violet.soft,
    border: `1px solid ${violet.buttonBorder}`,
    boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset',
    transition:
      'background-color 180ms ease, color 180ms ease, box-shadow 220ms ease, transform 160ms cubic-bezier(0.4, 0, 0.2, 1), border-color 180ms ease',
    '& .chip-refresh-icon': {
      transition: 'transform 520ms cubic-bezier(0.34, 1.56, 0.64, 1)'
    },
    '&:hover': {
      backgroundColor: violet.primary,
      borderColor: violet.primary,
      color: ink.refreshHover,
      boxShadow: `0 1px 0 rgba(255,255,255,0.2) inset, 0 6px 14px -4px ${violet.hoverShadow}`,
      '& .chip-refresh-icon': {
        transform: 'rotate(-120deg)'
      }
    },
    '&:active': {
      transform: 'translateY(0.5px)'
    },
    '&:focus-visible': {
      outline: `2px solid ${violet.focus}`,
      outlineOffset: 1
    }
  } as SxProps,

  refreshIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 12,
    height: 12
  } as SxProps,

  dismissButton: {
    all: 'unset',
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    marginLeft: '-2px',
    marginRight: '2px',
    borderRadius: '50%',
    color: ink.muted,
    transition: 'color 160ms ease, background-color 160ms ease',
    '&:hover': {
      color: ink.primary,
      backgroundColor: 'rgba(0, 0, 0, 0.045)'
    },
    '&:focus-visible': {
      outline: `2px solid ${violet.focus}`,
      outlineOffset: 1
    }
  } as SxProps
}

export default styles
